import { StudentPortalMode } from '@/types/composition-functions';

/**
 * Per-school student portal surface (issue #326).
 *
 * Schools flagged CBT_ONLY (set by a platform admin, exposed as
 * `School.student_portal_mode`) collapse the student portal to just the CBT
 * surface — menu, navigation and routing. This replaces the long-lived `utme`
 * frontend branch with a runtime flag.
 */

/** Route names a student may still visit when their school is CBT-only. */
export const CBT_ONLY_STUDENT_ROUTES: ReadonlySet<string> = new Set([
  'student-cbt',
  // the exam-taking page itself (/student/cbtpage/:id) — ActiveExams pushes here
  'cbt-page',
  'student-account-disabled',
]);

export function isCbtOnlyMode(mode: string | null | undefined): boolean {
  return mode === StudentPortalMode.CbtOnly;
}

/** The route a student lands on after login / on the bare `/student` path. */
export function studentLandingRoute(cbtOnly: boolean): 'student-cbt' | 'student-home' {
  return cbtOnly ? 'student-cbt' : 'student-home';
}

/** Whether a student route may be visited given the school's portal mode. */
export function isStudentRouteAllowed(routeName: unknown, cbtOnly: boolean): boolean {
  if (!cbtOnly) return true;
  return typeof routeName === 'string' && CBT_ONLY_STUDENT_ROUTES.has(routeName);
}

interface StudentMenuItemLike {
  routeName: string;
  showFor?: string;
}

/**
 * Shared filter for the student sidebar + mobile nav: applies the existing
 * tertiary/non-tertiary `showFor` rules, then collapses to the CBT-only
 * allowlist when the school's portal mode demands it.
 */
export function filterStudentMenuItems<T extends StudentMenuItemLike>(
  items: readonly T[],
  opts: { schoolType?: string | null; cbtOnly?: boolean },
): T[] {
  const schoolType = opts.schoolType?.toLowerCase();

  return items.filter(item => {
    if (opts.cbtOnly && !CBT_ONLY_STUDENT_ROUTES.has(item.routeName)) return false;

    if (!item.showFor || item.showFor === 'all') return true;

    if (item.showFor === 'tertiary') {
      return schoolType === 'tertiary';
    }

    if (item.showFor === 'non-tertiary') {
      return schoolType !== 'tertiary';
    }

    return false;
  });
}
